import {
  Box,
  Button,
  Flex,
  Form,
  SortableTable,
  SubmitButton,
  type TableColumnType,
} from '@/components';
import { useChangeOrderOfHierarchyLabel } from '@/features/tenant-manager/hierarchy-label/_hooks/hierarchy-label';
import type {
  HierarchyLabelModel,
  HierarchyLabelsModel,
} from '@/features/tenant-manager/hierarchy-label/_models/hierarchy-label';
import { useSortOrder } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  dataSource: z.infer<typeof HierarchyLabelsModel>;
  columns: Array<TableColumnType<z.infer<typeof HierarchyLabelModel>>>;
  onCancel: () => void;
  onSubmitSuccess: () => void;
};

export const HierarchyLabelSortableTable: FC<Props> = ({
  dataSource,
  columns,
  onCancel,
  onSubmitSuccess,
}) => {
  const { sortedData, onDragEnd, hasDataIndexChanged } =
    useSortOrder<z.infer<typeof HierarchyLabelModel>>(dataSource);
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { mutateAsync: updateSortOrderAsync } =
    useChangeOrderOfHierarchyLabel();

  const onSubmit = async () => {
    try {
      const params = sortedData.map((d, i) => ({
        id: d.id,
        sortOrder: i + 1,
      }));

      await updateSortOrderAsync(params);
      openMessage('success', t('message-update-completed'));
      onSubmitSuccess();
    } catch {
      openMessage('error', t('message-update-failed'));
    }
  };

  return (
    <Form
      leavePageAlertCondition={{
        additionalAlertCondition: hasDataIndexChanged,
      }}
      onFinish={onSubmit}
    >
      <Box marginBottom={12}>
        <Flex justify="flex-end" gap={4}>
          <Button onClick={onCancel}>{t('cancel')}</Button>
          <SubmitButton text={t('save')} disabled={!hasDataIndexChanged} />
        </Flex>
      </Box>

      <SortableTable<z.infer<typeof HierarchyLabelsModel>>
        dataSource={sortedData}
        columns={columns}
        rowKey="id"
        rowKeys={sortedData.map((d) => d.id)}
        onDragEnd={onDragEnd}
        pagination={false}
      />
    </Form>
  );
};
